import axios from 'axios';
import {baseUrl} from '@tenant/core/api/main/api';
import BadRequestError from 'Errors/BadRequestError';
import NotFoundError from 'Errors/NotFoundError';
import ValidationError from '../Errors/ValidationError';
import UnauthorizedError from '../Errors/UnauthorizedError';
import ServerError from '../Errors/ServerError';
import TwoFactorRequiredError from '../Errors/TwoFactorRequiredError';
import {getObjectProp, getCookie, deleteCookie} from '../utils/helpers';
import {KEY_AUTHENTICATED, KEY_AUTHENTICATION_TOKEN} from '../utils/constants';

const Service = axios.create({
  headers: {
    'X-Requested-With': 'XMLHttpRequest',
    Accept: 'application/json',
  },
  withCredentials: true,
});

/**
 * The base URL is resolved per request rather than at module scope:
 * `baseUrl()` reads through the platform adapter, which is only registered
 * during app bootstrap, so nothing here may call it while the module loads.
 */
Service.interceptors.request.use(config => ({
  ...config,
  baseURL: config.baseURL ?? baseUrl(),
}));

const handleSuccess = response => response.data;

const DEFAULT_ERROR_MESSAGE = `There appears to be a problem with your profile, but don't worry, a simple reset should sort it out. Please click on the link below and enter your email address. If the problem persists, contact info@tenant.co.nz for assistance.`;

const handleError = error => {
  const {response} = error;
  const message =
    getObjectProp(response, 'data.message') ||
    getObjectProp(response, 'message');
  if (response) {
    // eslint-disable-next-line default-case
    switch (response.status) {
      case 422:
        return Promise.reject(
          new ValidationError(response.data.errors, message, response.status)
        );
      case 428:
        return Promise.reject(
          new TwoFactorRequiredError(response.data, message)
        );
      case 429:
        return Promise.reject(
          new ServerError(
            response.status,
            message ||
              'Too many attempts. Please wait a few minutes and try again.'
          )
        );
      case 400:
        return Promise.reject(new BadRequestError(message));
      case 404:
        return Promise.reject(new NotFoundError(message));
      case 403:
      case 401:
        return Promise.reject(
          new UnauthorizedError(
            response.status,
            `${message}. You likely have too many TABS open causing an error with the system. Please LOG OUT, close all the other tabs, and try again.`
          )
        );
      default:
        return Promise.reject(
          new ServerError(
            response.status || 500,
            message ||
              `There appears to be a problem with your profile, but don't worry, a simple reset should sort it out. Please click on the link below and enter your email address. If the problem persists, contact info@tenant.co.nz for assistance.`,
            getObjectProp(response, 'data.errors') || {}
          )
        );
    }
  }
  // No HTTP response at all: network failure, timeout, or an error thrown by a
  // request interceptor. There is no status to read, so report a plain 500.
  return Promise.reject(new ServerError(500, DEFAULT_ERROR_MESSAGE));
};

Service.interceptors.response.use(handleSuccess, handleError);

// Service.interceptors.request.use(
//   config => config,
//   error => {
//     Promise.reject(error);
//   }
// );

Service.interceptors.request.use(
  config => {
    const auth = getCookie(KEY_AUTHENTICATED);
    const token = getCookie(KEY_AUTHENTICATION_TOKEN);
    if (token && auth === 'true') {
      // eslint-disable-next-line no-param-reassign, fp/no-mutation
      config.headers.Authorization = `Bearer ${token}`;
    } else if (!token && auth === 'true') {
      deleteCookie(KEY_AUTHENTICATED);
      // eslint-disable-next-line fp/no-mutation
      window.location.href = '/';
    }
    return config;
  },
  error => {
    Promise.reject(error);
  }
);

export default Service;
